import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormCheck } from "go-form";
import { LimitComponentVisibility } from "go-core/components/LimitComponentVisibility";
import { FormDirty } from "go-form/components/FormDirty";
import { FormSelectGroup } from "go-form/components/FormSelect";
import {
	ExternalProviderMenuConfigApi,
	ExternalProviderMenuConfigExtraMapper,
	ExternalProviderMenuConfigOptionsApi,
	MenuProvider,
} from "../../../../../../../../../../../services/Api/Organization/types";

interface Props {
	config: ExternalProviderMenuConfigApi | undefined;
	configOptions: ExternalProviderMenuConfigOptionsApi | undefined;
	setMenuConfigParams: React.Dispatch<React.SetStateAction<ExternalProviderMenuConfigApi | undefined>>;
	setForceFetchMenu: React.Dispatch<React.SetStateAction<boolean>>;
	setVisible: React.Dispatch<React.SetStateAction<boolean>>;
	visible: boolean;
	provider: MenuProvider;
}

const MenuExternalPortalSettings = ({
	config,
	configOptions,
	setMenuConfigParams,
	setForceFetchMenu,
	visible,
	setVisible,
	provider,
}: Props) => {
	const { t } = useTranslation();
	const form = useForm<ExternalProviderMenuConfigApi>({
		criteriaMode: "all",
		defaultValues: config,
	});
	const { control, register, watch, setValue, reset } = form;

	useEffect(() => {
		reset(config);
	}, [JSON.stringify(config), provider]);

	if (!config || !configOptions) return <></>;

	const variantModeOptions = configOptions.variant_modes.map((option) => ({
		label: option,
		value: option,
	}));
	const modifierModeOptions = configOptions.modifier_modes.map((option) => ({
		label: option,
		value: option,
	}));

	const getUpdatedExtraMappersFormValue = (
		extraMappersForm: { value: boolean; name: ExternalProviderMenuConfigExtraMapper }[],
		index: number,
		value: boolean
	) => {
		const copiedExtraMappersForm = [...extraMappersForm];
		copiedExtraMappersForm[index].value = value;

		return {
			extra_mappers_form: copiedExtraMappersForm,
			extra_mappers: copiedExtraMappersForm.filter((mapper) => mapper.value).map((mapper) => mapper.name),
		};
	};

	const handleClickExtraMapper = (event: React.MouseEvent<HTMLInputElement, MouseEvent>, index: number) => {
		const values = getUpdatedExtraMappersFormValue(watch("extra_mappers_form"), index, event.currentTarget.checked);
		const extraMappersForm = [...values.extra_mappers_form];
		const extraMappers = [...values.extra_mappers];
		setValue("extra_mappers_form", extraMappersForm);
		setValue("extra_mappers", extraMappers);
		setMenuConfigParams({
			...watch(),
			extra_mappers: extraMappers,
			extra_mappers_form: extraMappersForm,
		});
		setForceFetchMenu(true);
	};

	const handleOnChangeFlatPrices = (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
		setMenuConfigParams({
			...watch(),
			flat_prices: event.currentTarget.checked,
		});
		setForceFetchMenu(true);
	};

	const handleOnChangeSelectValue = (name: string, value: string) => {
		setMenuConfigParams({
			...watch(),
			[name]: value,
		});
		setForceFetchMenu(true);
	};

	const handleChangeVisible = (value?: boolean) => {
		if (value) {
			setMenuConfigParams(config);
			reset(config);
			setForceFetchMenu(true);
		}
	};

	return (
		<LimitComponentVisibility
			defaultVisible={visible}
			id="menu-external-portal-settings"
			isVisibleControl={visible}
			setIsVisibleControl={setVisible}
			legendText={t("modules.external_portal.field.advanced_settings.title")}
			helpText={t("modules.external_portal.field.advanced_settings.help_text")}
			handleChange={handleChangeVisible}
			unlimitedContent={
				<>
					<FormDirty isSubmitting={false} isDirty={false}>
						<FormSelectGroup
							errors={[]}
							control={control}
							name="variant_mode"
							options={variantModeOptions}
							defaultValue={{
								label: config.variant_mode,
								value: config.variant_mode,
							}}
							label={t("modules.external_portal.field.variant_mode.title")}
							help={t("modules.external_portal.field.variant_mode.help_text")}
							isClearable={false}
							onChange={(value) => handleOnChangeSelectValue("variant_mode", value)}
						/>
						<FormSelectGroup
							errors={[]}
							control={control}
							name="modifier_mode"
							options={modifierModeOptions}
							defaultValue={{
								label: config.modifier_mode,
								value: config.modifier_mode,
							}}
							label={t("modules.external_portal.field.modifier_mode.title")}
							help={t("modules.external_portal.field.modifier_mode.help_text")}
							isClearable={false}
							onChange={(value) => handleOnChangeSelectValue("modifier_mode", value)}
						/>
						{configOptions.flat_prices && (
							<FormCheck
								name="flat_prices"
								errors={[]}
								register={register}
								label={t("modules.external_portal.field.flat_price.title")}
								help={t("modules.external_portal.field.flat_price.help_text")}
								onClick={handleOnChangeFlatPrices}
							/>
						)}
						{(configOptions.extra_mappers || []).map((option, optionIndex) => {
							const index = config.extra_mappers_form?.findIndex((item) => item.name === option);
							if (index > -1)
								return (
									<FormCheck
										key={`${provider}-${option}-${index}`}
										name={`extra_mappers_form.${index}.value`}
										errors={[]}
										onClick={(event) => handleClickExtraMapper(event, index)}
										register={register}
										label={t(`modules.external_portal.field.${option.toLowerCase()}.title`)}
										help={t(`modules.external_portal.field.${option.toLowerCase()}.help_text`)}
									/>
								);
							return (
								<FormCheck
									key={`${provider}-${option}-${index}`}
									name={`extra_mappers_form.${optionIndex}.value`}
									errors={[]}
									register={register}
									className="hidden"
								/>
							);
						})}
					</FormDirty>
				</>
			}
		/>
	);
};

export default MenuExternalPortalSettings;

// t('modules.external_portal.field.split_required_options.title')
// t('modules.external_portal.field.split_required_options.help_text')
// t('modules.external_portal.field.clean_option_default_quantity.title')
// t('modules.external_portal.field.clean_option_default_quantity.help_text')
// t('modules.external_portal.field.skip_option.title')
// t('modules.external_portal.field.skip_option.help_text')
// t('modules.external_portal.field.split_group_with_min_permitted.title')
// t('modules.external_portal.field.split_group_with_min_permitted.help_text')
// t('modules.external_portal.field.clean_group_charge_above.title')
// t('modules.external_portal.field.clean_group_charge_above.help_text')
